'use client';

import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useHandleError } from '@/hooks/useHandleError';
import { apiFetch } from '@/lib/api/apiClient';
import { useModalStore } from '@/stores/useModalStore';
import { AwardAchievementItems } from '@/types/achievements';
import { TopResponse } from '@/types/top';
import ServiceLayout from '@/components/layout/service/ServiceLayout';
import AwardBadgeNotification from '@/components/modal/AwardBadgeNotification';
import AwardMedalNotification from '@/components/modal/AwardMedalNotification';
import TopAnnouncements from '@/(routers)/top/components/TopAnnouncements';
import TopHero from '@/(routers)/top/components/TopHero';
import TopNewsFesta from '@/(routers)/top/components/TopNewsFesta';
import TopProjectStory from '@/(routers)/top/components/TopProjectStory';
import TopStories from '@/(routers)/top/components/TopStories';
import TopVideoGallery from '@/(routers)/top/components/TopVideoGallery';

export interface TopProps {
    initialTopData: TopResponse | null;
}

export default function Top({ initialTopData }: TopProps) {
    const { openModal } = useModalStore();
    const { handleError } = useHandleError();

    const [userAwardAchievementMedal, setUserAwardAchievementMedal] = useState<AwardAchievementItems | null>(null);
    const [userAwardAchievementBadge, setUserAwardAchievementBadge] = useState<AwardAchievementItems | null>(null);
    const [topData, setTopData] = useState<TopResponse | null>(initialTopData);

    const videoGalleryRef = useRef<HTMLDivElement | null>(null);
    const handleArrowClick = () => {
        videoGalleryRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        if (!topData) {
            (async () => {
                try {
                    const data = await apiFetch(`/top`);
                    setTopData(data);
                } catch (error: unknown) {
                    handleError(error);
                }
            })();
        }
        (async () => {
            try {
                const data = await apiFetch(`/users/me/achievement/award/medal`);
                setUserAwardAchievementMedal(data);
            } catch (error: unknown) {
                handleError(error);
            }
        })();
        (async () => {
            try {
                const data = await apiFetch(`/users/me/achievement/award/badge`);
                setUserAwardAchievementBadge(data);
            } catch (error: unknown) {
                handleError(error);
            }
        })();
    }, []);

    useEffect(() => {
        if (userAwardAchievementMedal !== null && userAwardAchievementMedal?.award_medals.length !== 0) {
            openModal({
                id: 'award-achievement-medal',
                component: <AwardMedalNotification />,
                size: {
                    pcWidth: '600px',
                    mbWidth: '90%',
                    pcHeight: '520px',
                    mbHeight: '80%',
                },
                direction: 'clip',
                overFlowType: 'auto',
            });
        }
    }, [userAwardAchievementMedal]);

    useEffect(() => {
        if (userAwardAchievementBadge !== null && userAwardAchievementBadge?.award_badges.length !== 0) {
            openModal({
                id: 'award-achievement-badge',
                component: <AwardBadgeNotification />,
                size: {
                    pcWidth: '600px',
                    mbWidth: '90%',
                    pcHeight: '520px',
                    mbHeight: '80%',
                },
                direction: 'clip',
                overFlowType: 'auto',
            });
        }
    }, [userAwardAchievementBadge]);

    const hasStories = Array.isArray(topData?.stories) && topData.stories.length > 0;

    return (
        <ServiceLayout>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5, delay: 0.0 }}
                className="flex flex-col justify-center items-center w-full w-full"
            >
                <div className="flex w-full">
                    <TopHero onScrollClick={handleArrowClick} />
                </div>
                <div className="flex w-full" ref={videoGalleryRef}>
                    <TopVideoGallery movies={topData?.movies ?? []} />
                </div>
                <div className="flex w-full">
                    <TopAnnouncements announcements={topData?.announcements ?? []} />
                </div>
                {hasStories ? (
                    <div className="flex w-full">
                        <TopStories stories={topData?.stories ?? []} />
                    </div>
                ) : (
                    <div className="flex w-full">
                        <TopProjectStory projectStories={topData?.project_stories ?? []} />
                    </div>
                )}
                <div className="section-spacer-inner-top-y"></div>
            </motion.div>
        </ServiceLayout>
    );
}
